import { startOfYesterday } from 'date-fns';
import React from 'react';

import {
  CmBox,
  CmButton,
  CmFlex,
  CmLabelAndMultipleReactSelect,
  CmStack,
  useMultipleReactSelectInput,
} from '@/components/uiParts';
import { DeliveryServices } from '@/domain/models/DeliveryService';
import { Enterprise } from '@/domain/models/Enterprise';
import { DeliveryType, IsValidDeliveryType } from '@/domain/models/Order';
import { SalesFilter } from '@/services/SalesReport/hooks';
import {
  useSalesItemState,
  useSalesRateState,
  useSalesReportState,
} from '@/store/sales_aggregation';

import { DateRangeFormControl } from './DateRangeFormControl';
import { SelectOption } from './SelectOptions';

export const SalesFilterFormHeight = 272;
export const ORDER_FILTER_FORM_ID = 'order-filter-form';

interface Props {
  enterprise: Enterprise;
  deliveryServices: DeliveryServices;
  orderCount: number;
  filter: SalesFilter;
  handleSubmit: (filter: SalesFilter) => void;
  page: 'sales' | 'items' | 'rate';
}

export const SalesParamsForm: React.VFC<Props> = ({
  enterprise,
  deliveryServices,
  orderCount,
  filter,
  handleSubmit,
  page,
}) => {
  const useSalesState = () => {
    switch (page) {
      case 'sales':
        return useSalesReportState();
      case 'items':
        return useSalesItemState();
      case 'rate':
        return useSalesRateState();
    }
  };

  const {
    from: from,
    to: to,
    masterBrandIDs,
    masterStoreIDs,
    deliveryServiceIds,
    deliveryTypes,
    authStoreIds,
    setFrom,
    setTo,
    setMasterBrandIDs,
    setMasterBrandNames,
    setMasterStoreIDs,
    setDeliveryServiceIds,
    setDeliveryTypes,
    setAuthStoreIds,
    setShouldFetch,
  } = useSalesState();

  const {
    authStoreOptions,
    masterBrandOptions,
    masterStoreOptions,
    deliveryServiceOptions,
  } = SelectOption.createSelectOptions(enterprise, deliveryServices);

  const brandSelectInput = useMultipleReactSelectInput(
    masterBrandOptions,
    masterBrandIDs
  );

  const storeSelectInput = useMultipleReactSelectInput(
    masterStoreOptions,
    masterStoreIDs
  );

  const deliveryServiceSelectInput = useMultipleReactSelectInput(
    deliveryServiceOptions,
    deliveryServiceIds
  );

  const deliveryTypeSelectInput = useMultipleReactSelectInput(
    DeliveryType,
    deliveryTypes
  );

  const authStoreSelectInput = useMultipleReactSelectInput(
    authStoreOptions,
    authStoreIds
  );

  const submit: React.FormEventHandler = (e) => {
    e.preventDefault();

    setFrom(from);
    setTo(to);
    setMasterBrandIDs(
      brandSelectInput.value?.map((brand) => brand.value) ?? []
    );
    setMasterBrandNames(
      brandSelectInput.value?.map((brand) => brand.label) ?? []
    );
    setMasterStoreIDs(
      storeSelectInput.value?.map((store) => store.value) ?? []
    );
    setDeliveryServiceIds(
      deliveryServiceSelectInput.value?.map((ds) => ds.value) ?? []
    );
    setDeliveryTypes(
      deliveryTypeSelectInput.value
        ?.map((type) => type.value)
        .filter(IsValidDeliveryType) ?? []
    );
    setAuthStoreIds(
      authStoreSelectInput.value?.map((authStore) => authStore.value) ?? []
    );
    setShouldFetch(true);
  };

  const onReset: React.FormEventHandler = (e) => {
    e.preventDefault();

    const resetStartDate = startOfYesterday();
    const resetEndDate = startOfYesterday();

    setFrom(resetStartDate);
    setTo(resetEndDate);

    brandSelectInput.onReset();
    authStoreSelectInput.onReset();
    storeSelectInput.onReset();
    deliveryServiceSelectInput.onReset();
    deliveryTypeSelectInput.onReset();
  };

  return (
    <CmBox as="form" onSubmit={submit} py={2} id={ORDER_FILTER_FORM_ID}>
      <DateRangeFormControl
        label="期間"
        startDate={new Date(from)}
        setStartDate={(date) => handleSubmit({ ...filter, from: date })}
        endDate={new Date(to)}
        setEndDate={(date) => handleSubmit({ ...filter, to: date })}
      />
      <CmStack isInline spacing={2} mt={3}>
        <CmLabelAndMultipleReactSelect
          label="ブランド"
          name="brandName"
          selectInput={brandSelectInput}
        />
        <CmLabelAndMultipleReactSelect
          label="拠点"
          name="auth_store_name"
          selectInput={authStoreSelectInput}
        />
        <CmLabelAndMultipleReactSelect
          label="店舗"
          name="storeName"
          selectInput={storeSelectInput}
        />
        <CmLabelAndMultipleReactSelect
          label="デリバリーサービス"
          name="deliveryService"
          selectInput={deliveryServiceSelectInput}
        />
      </CmStack>
      <CmFlex justifyContent="space-between" alignItems="center" mt={3}>
        <div>
          <CmButton onClick={onReset} variant="outline">
            クリア
          </CmButton>
          <CmButton type="submit" ml={3}>
            絞り込み
          </CmButton>
        </div>
        <CmBox mt={3}>注文件数: {orderCount} 件</CmBox>
      </CmFlex>
    </CmBox>
  );
};
